import { FC, PropsWithChildren, useEffect, useState, useCallback } from 'react'
import { useMaasOidcAccessToken } from '@maas-tools/auth-oidc'
import { useSnackbar } from 'notistack'
import { OPENID_LOGOUT_URL } from 'common/constants/env'
import { IdmEvents, IdmEventsMap } from 'common/idm/events'
import { useLogout } from 'common/infra/logout'
import { AppWebStorageKey } from 'common/store/constants'
import AccountService from 'services/account/http'
import { SessionReliabilityInferOptions } from 'services/account/types'
import { TSessionReliabilityGuardProps } from './types'

const SessionReliabilityGuard: FC<PropsWithChildren<TSessionReliabilityGuardProps>> = ({
  children,
  LoaderComponent,
}) => {
  const webStorage = window.sessionStorage
  const [loading, setLoading] = useState<boolean>(true)
  const logout = useLogout()
  const { enqueueSnackbar } = useSnackbar()

  const { accessTokenPayload } = useMaasOidcAccessToken()

  const handleSessionValidation = useCallback(async () => {
    const sessionReliabilityValue = webStorage.getItem(AppWebStorageKey.ReliabilityStatus)
    const sessionReliabilityOwner = webStorage.getItem(AppWebStorageKey.SessionOwnerId)

    let shouldCheck = true

    // Verifica se o resultado anterior foi "trusted" e se o proprietário da sessão é o mesmo
    if (
      sessionReliabilityValue === SessionReliabilityInferOptions.TRUSTED &&
      sessionReliabilityOwner === accessTokenPayload.jti
    ) {
      shouldCheck = false
    }

    if (shouldCheck) {
      try {
        const validateResult = await AccountService.getSessionReliabilityInfer()

        if (validateResult.result === 'UNTRUSTED') {
          // Armazena no sessionStorage que o usuário não é confiável
          webStorage.setItem(
            AppWebStorageKey.ReliabilityStatus,
            SessionReliabilityInferOptions.UNTRUSTED
          )
          await logout(`${OPENID_LOGOUT_URL}?error_login=${validateResult.message}`)
        } else {
          // A propriedade 'jti', extraída do token de acesso, identifica de forma única uma sessão. Isso resulta na revalidação da sessão pelo TMX sempre que uma nova sessão é iniciada.
          webStorage.setItem(AppWebStorageKey.SessionOwnerId, accessTokenPayload.jti as string)
          webStorage.setItem(AppWebStorageKey.ReliabilityStatus, validateResult.result)
        }
      } catch (err) {
        enqueueSnackbar(err.message, {
          variant: 'error',
          persist: true,
        })
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [accessTokenPayload.jti, enqueueSnackbar, logout, webStorage])

  useEffect(() => {
    const onLogout = () => {
      sessionStorage.removeItem(AppWebStorageKey.ReliabilityStatus)
      sessionStorage.removeItem(AppWebStorageKey.SessionOwnerId)
    }

    handleSessionValidation()

    IdmEvents.addEventListener(IdmEventsMap.USER.LOGGED_OUT, onLogout)
    // Limpeza dos eventos
    return () => {
      IdmEvents.removeEventListener(IdmEventsMap.USER.LOGGED_OUT, onLogout)
    }
  }, [handleSessionValidation])

  if (loading) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{LoaderComponent}</>
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

export default SessionReliabilityGuard
